import React, { useCallback, useMemo } from 'react'
import { FormProvider, useFormContext, UseFormReturn } from 'react-hook-form'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { Panel } from '@components/Panel/Panel'
import { AccountAbstractionConfig, UserOps } from '@towns/userops'
import { FancyButton, FormRender, Stack, Text } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useBalance } from 'hooks/useBalance'
import { useEnvironment } from 'hooks/useEnvironment'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    BaseTransactor,
    BlockchainTransactionType,
    TransactionStatus,
    useBaseReads,
    useBaseWrites,
    useMyLinkedWallets,
    useSanctionedAddresses,
    useTownsContext,
} from 'use-towns-client'
import { Address } from 'viem'

import { useUSDCMetadata } from '../UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { EthInputField, getValidatedData } from '../Wallet/TransferAssetsPanel'
import { TransferSchema, transferSchema } from '../Wallet/transferAssetsSchema'
import { WalletSelector } from '../Wallet/WalletSelector'
import { WalletWithBalance } from '../Wallet/WalletWithBalance'
import { FullPanelOverlay } from '../WalletLinkingPanel'

import { ConfirmationModal } from './ConfirmationModal'
import { useGetSimpleAccountAddress } from './hooks'

export function TransferFromNonDeployedSimpleAccountForm() {
    const { data: simpleAccount } = useGetSimpleAccountAddress()
    const { data: primaryAAAddress } = useMyAbstractAccountAddress()
    const { data: linkedWallets, isLoading: isLinkedWalletsLoading } = useMyLinkedWallets()

    const { data: fromBalance } = useBalance({
        address: simpleAccount?.address,
        enabled: !!simpleAccount?.address,
    })

    // in case they also have not deployed their modular account, list it as well
    const walletsToSendTo: string[] = useMemo(() => {
        const all: string[] = []
        if (primaryAAAddress) {
            all.push(primaryAAAddress)
        }
        if (linkedWallets) {
            all.push(...linkedWallets)
        }
        return Array.from(new Set(all))
    }, [linkedWallets, primaryAAAddress])

    if (!simpleAccount?.address) {
        return null
    }

    if (isLinkedWalletsLoading) {
        return <ButtonSpinner />
    }

    return (
        <Panel>
            <Stack gap="lg">
                <Stack gap background="level2" rounded="md" padding="md">
                    <WalletWithBalance address={simpleAccount.address} isAbstractAccount={false} />
                </Stack>

                <FormRender
                    gap
                    schema={transferSchema}
                    defaultValues={{
                        assetAddressOrIdentifier: 'BASE_ETH',
                    }}
                    id="TransferEthFromNonDeployedAccount"
                    mode="onChange"
                    height="100%"
                >
                    {(form: UseFormReturn<TransferSchema>) => {
                        const isSubmitting = form.formState.isSubmitting

                        return (
                            <FormProvider {...form}>
                                <EthInputField fromBalance={fromBalance} />

                                <Stack gap>
                                    <Text strong>Send to</Text>
                                    <WalletSelector
                                        aaAddress={primaryAAAddress}
                                        linkedWallets={walletsToSendTo}
                                        fromWallet={simpleAccount.address}
                                    />
                                </Stack>

                                <SubmitButton simpleAccountAddress={simpleAccount.address} />
                                {isSubmitting && <FullPanelOverlay withSpinner />}
                            </FormProvider>
                        )
                    }}
                </FormRender>
                <ConfirmationModal address={simpleAccount.address} />
            </Stack>
        </Panel>
    )
}

function SubmitButton(props: { simpleAccountAddress: Address }) {
    const { simpleAccountAddress } = props
    const { handleSubmit, formState, watch, setError, reset } = useFormContext<TransferSchema>()
    const allValues = watch()
    const { assetAddressOrIdentifier, ethAmount, recipient } = allValues
    const { userOpsInstance } = useBaseWrites()
    const environment = useEnvironment()
    const { baseProvider, baseConfig } = useTownsContext()
    const { baseTransactor: primaryBaseTransactor } = useBaseWrites()
    const { data: primaryAAAddress } = useMyAbstractAccountAddress()
    const baseReads = useBaseReads()
    const { isSanctioned } = useSanctionedAddresses()
    const { address: usdcAddress } = useUSDCMetadata()

    const isDisabled = useMemo(() => {
        if (
            formState.isSubmitting ||
            !assetAddressOrIdentifier ||
            (assetAddressOrIdentifier === 'BASE_ETH' &&
                (formState.errors.ethAmount || !ethAmount)) ||
            !recipient
        ) {
            return true
        }
        return false
    }, [
        formState.isSubmitting,
        formState.errors.ethAmount,
        assetAddressOrIdentifier,
        ethAmount,
        recipient,
    ])

    const onValid = useCallback(
        async (data: TransferSchema, getSigner: GetSigner) => {
            if (!environment.accountAbstractionConfig) {
                return
            }

            const signer = await getSigner()
            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }

            const dataToSubmit = getValidatedData({
                data,
                setError,
                source: simpleAccountAddress,
                isSanctioned,
                usdcAddress,
            })

            if (!dataToSubmit) {
                return
            }

            if (!primaryBaseTransactor || !primaryAAAddress) {
                return
            }

            // deploy a modular account first
            const deployedModularAccount =
                await primaryBaseTransactor.spaceDapp.provider.getCode(primaryAAAddress)
            if (deployedModularAccount === '0x') {
                console.log('[SimpleAccountTransfer] deploying modular account')

                const deployOp = await primaryBaseTransactor?.createTransaction({
                    signer,
                    type: BlockchainTransactionType.ZeroBurn,
                    debugName: 'sendZeroBurnOp',
                    aaExecute: () => userOpsInstance?.sendZeroBurnOp(signer),
                    eoaExecute: () => {
                        throw new Error('Not implemented')
                    },
                    errorDecoder: (error) => {
                        return error as Error
                    },
                })

                const deployReceipt =
                    await primaryBaseTransactor?.waitForBlockchainTransaction(deployOp)

                if (deployReceipt?.status === TransactionStatus.Failed) {
                    console.log(
                        '[SimpleAccountTransfer] modular account deployment failed',
                        deployReceipt,
                    )
                    return
                } else {
                    console.log('[SimpleAccountTransfer] modular account deployed', deployReceipt)
                }
            }

            const simpleAccountAbstractionConfig: AccountAbstractionConfig = {
                ...environment.accountAbstractionConfig,
                newAccountImplementationType: 'simple',
                forceSmartAccountType: 'simple',
                forceSmartAccountAddress: simpleAccountAddress,
            }

            const simpleAccountUserOps = new UserOps({
                ...simpleAccountAbstractionConfig,
                provider: baseProvider,
                config: baseConfig,
                spaceDapp: baseReads.spaceDapp,
                appRegistryDapp: baseReads.appRegistryDapp,
            })
            const simpleAccountBaseTransactor = new BaseTransactor({
                baseProvider: baseProvider,
                baseConfig: baseConfig,
                baseReads,
                createLegacySpaces: false,
                userOps: simpleAccountUserOps,
            })

            console.log('[SimpleAccountTransfer] transferring asset', dataToSubmit)
            const tx = await simpleAccountBaseTransactor.transferAsset(dataToSubmit, signer)
            const receipt = await simpleAccountBaseTransactor.waitForBlockchainTransaction(tx)

            if (receipt?.status === TransactionStatus.Failed) {
                console.log('[SimpleAccountTransfer] transfer failed', receipt)
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        message="There was an error transferring your assets."
                        toast={toast}
                    />
                ))
                return
            } else {
                popupToast(({ toast }) => (
                    <StandardToast.Success
                        message="Your assets have been transferred successfully."
                        toast={toast}
                    />
                ))
                reset()
            }
        },
        [
            environment.accountAbstractionConfig,
            setError,
            simpleAccountAddress,
            isSanctioned,
            usdcAddress,
            primaryBaseTransactor,
            primaryAAAddress,
            baseProvider,
            baseConfig,
            baseReads,
            userOpsInstance,
            reset,
        ],
    )
    return (
        <WalletReady>
            {({ getSigner }) => (
                <FancyButton
                    borderRadius="lg"
                    cta={!isDisabled}
                    disabled={isDisabled}
                    onClick={handleSubmit((data) => onValid(data, getSigner))}
                >
                    Transfer
                </FancyButton>
            )}
        </WalletReady>
    )
}
